import React, { useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase";
import Window from "./Window";
import Icon from "./Icon";
import { useBlocks } from "../blocks";
import {
  MAX_NOTE,
  REPORT_REASONS,
  ReportTarget,
  RequestResult,
  blockUser,
  findUserByUsername,
  removePenPal,
  requestResultMessage,
  submitReport,
  unblockUser,
} from "../penpal";
import { isSlowDown } from "../pace";

// "Send pen pal request" that opens a small note box first.
export function RequestButton({
  send,
  className = "btn",
  label = "Send pen pal request",
}: {
  send: (note: string) => Promise<RequestResult>;
  className?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RequestResult | null>(null);
  const [failed, setFailed] = useState("");

  if (result) {
    return (
      <p className={result === "sent" ? "success-text" : "notice text-sm"} role="status">
        {requestResultMessage[result]}
      </p>
    );
  }

  if (!open) {
    return (
      <button type="button" className={className} onClick={() => setOpen(true)}>
        <Icon name="request" size={16} /> {label}
      </button>
    );
  }

  return (
    <form
      className="flex w-full flex-col gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setFailed("");
        try {
          const r = await send(note);
          if (r !== "slow-down") setResult(r);
        } catch (err) {
          console.error(err);
          setFailed(
            auth.currentUser?.emailVerified
              ? "Sorry, that didn't send. Please try again."
              : "Please verify your email first. Use the reminder at the top of the page to resend the link."
          );
        }
        setBusy(false);
      }}
    >
      <label className="field">
        <span className="field-label">Say hi (optional)</span>
        <textarea
          className="input !min-h-[5rem]"
          value={note}
          maxLength={MAX_NOTE}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What made you want to write to them?"
          autoFocus
        />
      </label>
      {failed && <p className="error-text">{failed}</p>}
      <div className="btn-row">
        <button type="submit" className="btn btn-sm" disabled={busy}>
          <Icon name="send" size={14} /> Send request
        </button>
        <button type="button" className="btn btn-plain btn-sm" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// End a pen pal connection without blocking (when the vibe isn't right).
export function RemovePenPalButton({
  otherUid,
  username,
  onRemoved,
  className = "btn btn-plain btn-sm",
}: {
  otherUid: string;
  username?: string;
  onRemoved?: () => void;
  className?: string;
}) {
  const [user] = useAuthState(auth);
  const [busy, setBusy] = useState(false);
  if (!user || user.uid === otherUid) return null;
  const who = username ? `@${username}` : "this pen pal";

  const remove = async () => {
    const ok = window.confirm(
      `Remove ${who} as a pen pal?\n\n` +
        "Neither of you will be able to send new letters. Your past letters stay in both inboxes. " +
        "They won't be told, and either of you can send a new pen pal request later.\n\n" +
        "(If someone is making you feel unsafe, Block or Report them instead.)"
    );
    if (!ok) return;
    setBusy(true);
    try {
      await removePenPal(user.uid, otherUid);
      onRemoved?.();
    } catch (err) {
      console.error(err);
      alert("Sorry, that didn't work. Please try again.");
    }
    setBusy(false);
  };

  return (
    <button type="button" className={className} onClick={remove} disabled={busy}>
      <Icon name="close" size={14} /> {busy ? "Removing..." : "Remove pen pal"}
    </button>
  );
}

// Small "Report" / "Block" links for a person, ad, or letter.
export function SafetyActions({
  target,
  canBlock = true,
}: {
  target: ReportTarget;
  canBlock?: boolean;
}) {
  const [user] = useAuthState(auth);
  const { ids } = useBlocks();
  const [reporting, setReporting] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || target.userId === user.uid) return null;
  const blocked = !!target.userId && ids.has(target.userId);
  const who = target.username ? `@${target.username}` : "this person";

  const toggleBlock = async () => {
    if (!target.userId) return;
    if (blocked) {
      setBusy(true);
      await unblockUser(user.uid, target.userId);
      setBusy(false);
      return;
    }
    const ok = window.confirm(
      `Block ${who}?\n\nThey won't be able to send you requests, letters, or kudos, ` +
        "you'll stop seeing their ads and messages, and if you're pen pals that ends. " +
        "They won't be told. You can unblock them later from My Profile."
    );
    if (!ok) return;
    setBusy(true);
    try {
      await blockUser(user.uid, target.userId, target.username || "");
    } catch (err) {
      console.error(err);
      alert("Sorry, blocking didn't work. Please try again.");
    }
    setBusy(false);
  };

  return (
    <>
      <span className="safety-actions">
        <button type="button" onClick={() => setReporting(true)}>
          <Icon name="flag" size={14} /> Report
        </button>
        {canBlock && target.userId && (
          <button type="button" onClick={toggleBlock} disabled={busy}>
            <Icon name="lock" size={14} /> {blocked ? `Unblock ${who}` : `Block ${who}`}
          </button>
        )}
      </span>
      {reporting && (
        <ReportDialog target={target} onClose={() => setReporting(false)} />
      )}
    </>
  );
}

// The report form. With no person picked (from the rules page), it asks
// who the report is about.
export function ReportDialog({
  target,
  onClose,
}: {
  target: ReportTarget;
  onClose: () => void;
}) {
  const [user] = useAuthState(auth);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [aboutName, setAboutName] = useState("");
  // A general report (from the rules page) isn't about any particular post or person.
  const general = !Object.values(target).some(Boolean);
  const [state, setState] = useState<"form" | "sending" | "sent" | "error">("form");
  const [problem, setProblem] = useState("");
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !reason) return;
    setProblem("");
    const name = aboutName.trim().replace(/^@/, "");
    // From the rules page, a report needs a person or a description.
    if (general && !name && !details.trim()) {
      setProblem("Please add their username, or tell us what happened.");
      return;
    }
    setState("sending");
    try {
      let about = target;
      if (general && name) {
        const found = await findUserByUsername(name);
        if (!found) {
          setProblem(`We couldn't find an account called @${name}. Check the spelling?`);
          setState("form");
          return;
        }
        about = { ...target, ...found };
      }
      await submitReport(user.uid, about, reason, details);
      setState("sent");
    } catch (err) {
      if (isSlowDown(err)) return setState("form"); // the wait note is showing
      console.error(err);
      setState("error");
    }
  };

  return (
    <div
      className="viewer-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Report"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="viewer-letter">
        <Window title="report.exe" tone="tan" minimizable={false}>
          {state === "sent" ? (
            <div className="flex flex-col items-start gap-3">
              <h2 className="window-heading">Thank you for telling us</h2>
              <p>
                A real person on our team will read your report and decide what
                to do. If you don't want to hear from someone anymore, you can
                also block them from their profile.
              </p>
              <p className="text-sm text-ink-soft">
                If you're in danger, please contact local emergency services.
              </p>
              <button ref={closeRef} type="button" className="btn" onClick={onClose} autoFocus>
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3">
              <h2 className="window-heading">Report a problem</h2>
              <p className="text-sm text-ink-soft">
                Every report is read and reviewed by a real person on our team,
                not a bot or an automatic filter. Our team can see who sent a
                report so we can follow up with you. The person you report is
                never told.
              </p>
              {general && (
                <label className="field">
                  <span className="field-label">Who is this about?</span>
                  <input
                    className="input"
                    value={aboutName}
                    maxLength={40}
                    onChange={(e) => setAboutName(e.target.value)}
                    placeholder="@username"
                    autoComplete="off"
                  />
                  <span className="text-xs text-ink-soft">
                    Their username, if it's about a person. If it isn't, describe
                    what happened below.
                  </span>
                </label>
              )}
              <fieldset className="grid gap-1 sm:grid-cols-2">
                <legend className="field-label mb-1">What's going on?</legend>
                {REPORT_REASONS.map((r) => (
                  <label key={r} className="check">
                    <input
                      type="radio"
                      name="reason"
                      value={r}
                      checked={reason === r}
                      onChange={() => setReason(r)}
                    />
                    <span className="text-sm font-semibold">{r}</span>
                  </label>
                ))}
              </fieldset>
              <label className="field">
                <span className="field-label">
                  {general ? "What happened?" : "Anything else? (optional)"}
                </span>
                <textarea
                  className="input"
                  value={details}
                  maxLength={2000}
                  onChange={(e) => setDetails(e.target.value)}
                />
              </label>
              {problem && (
                <p className="error-text" role="alert">
                  {problem}
                </p>
              )}
              {state === "error" && (
                <p className="error-text">Sorry, that didn't send. Please try again.</p>
              )}
              <div className="btn-row">
                <button type="submit" className="btn" disabled={!reason || state === "sending"}>
                  <Icon name="flag" size={16} /> Send report
                </button>
                <button type="button" className="btn btn-plain" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </Window>
      </div>
    </div>
  );
}
